import { MapService } from "@services/map.service";

export interface IIsochroneParameters {
    lat: number;
    lng: number;
    mode: google.maps.TravelMode;
    type: "distance" | "duration";
    value: number; // i.e. distance or duration

    slices?: number;
    cycles?: number;
    precision?: number;

    system?: google.maps.UnitSystem;
    callback: (x: google.maps.DistanceMatrixStatus | string, positions: IIsochronePosition[]) => void,
}

export interface IIsochroneState {
    errors: number;
    slices: number;
    cycles: number;
    cycle: number;
    lat: number;
    lng: number;
    type: string;
    value: number;
    mode: google.maps.TravelMode;
    system: google.maps.UnitSystem;
    precision: number;
    callback: (x: google.maps.DistanceMatrixStatus | string, y: IIsochronePosition[]) => void;
    positions: IIsochronePosition[];
}

export interface IIsochronePosition {
    distance: number;
    duration: number;
    found: boolean;
    lat: number;
    lng: number;
    radians: number;
    radius: number;

    min: { radius: number; value: number; };
    max: { radius: number; value: number; };
}

/*! Isochrone and isodistance drawer for Google Maps by www.dugwood.com */
export class IsochroneService {

    service: google.maps.DistanceMatrixService;

    constructor() {
    }

    public compute(parameters: IIsochroneParameters) {

        this.initialise();

        var requests = 0;
        var ready = false;
        var callback: () => void;
        var debug = false;

        var state: IIsochroneState = {
            errors: 0,
            slices: 0,
            cycles: 0,
            cycle: 0,
            lat: 0,
            lng: 0,
            type: '',
            value: 0,
            mode: google.maps.TravelMode.DRIVING,
            system: google.maps.UnitSystem.IMPERIAL,
            precision: 0,
            callback: (x, y) => { },
            positions: [] as IIsochronePosition[]
        };

        var radius = {// These are approximate values, so that the first point is not that far from the final value
            duration: {
                walking: 11,
                bicycling: 30,
                driving: 60,
                transit: 10
            },
            distance: {
                walking: 9,
                bicycling: 8,
                driving: 7,
                transit: 7
            }
        };

        if (!parameters.callback) {
            //computation.callback = () => { };
            return this.computationCallback(state, 'KO', 'Missing callback');
        };

        state.errors = 0;
        state.callback = parameters.callback;

        //if (!ready) {
        //    return this.computationCallback(state, 'KO', 'Not ready, call init() or wait for init to be callbacked');
        //}

        state.slices = parameters.slices || 8;
        if (state.slices < 4 || state.slices > 25) {
            this.computationCallback(state, 'KO', 'Wrong value for slices: between 2 and 25');
        }

        state.cycle = 0;
        state.cycles = parameters.cycles || 10;

        if (state.cycles < 2 || state.cycles > 50) {
            return this.computationCallback(state, 'KO', 'Wrong value for cycles: between 2 and 50');
        }

        if (!parameters.lat || !parameters.lng) {
            return this.computationCallback(state, 'KO', 'Missing lat and lng parameters');
        }

        state.lat = parameters.lat;
        state.lng = parameters.lng;
        state.mode = parameters.mode || google.maps.TravelMode.DRIVING;
        state.type = parameters.type || "distance";
        state.value = parameters.value || 0;

        state.precision = (parameters.precision || 5) / 100;
        state.system = parameters.system && parameters.system === google.maps.UnitSystem.IMPERIAL ? google.maps.UnitSystem.IMPERIAL : google.maps.UnitSystem.METRIC;

        /* Cut the circle in «slices» */
        state.positions = [];
        var radiusValue = radius[state.type][state.mode] * state.value / 1000000;

        if (state.system === google.maps.UnitSystem.IMPERIAL) {
            radiusValue *= 0.9144;
        }

        for (var s = 0; s < state.slices; s++) {
            state.positions.push({
                radians: 2 * Math.PI * s / state.slices,
                min: {
                    radius: 0,
                    value: 0
                },
                max: {
                    radius: 0,
                    value: 0
                },
                /* Radius is really an approximate value */
                radius: 0.05,
                lat: 0,
                lng: 0,
                duration: 0,
                distance: 0,
                found: false
            });
        }

        this.cycle(state);
    }

    // --------------------
    
    private initialise() {
        if (this.service) {
            return;
        }

        this.service = new google.maps.DistanceMatrixService();
    }

    private cycle(state: IIsochroneState) {
        //console.log("cycle");

        if (state.cycle++ >= state.cycles) {
            return this.computationCallback(state, 'OK');
        }

        var p = 0,
            position,
            destinations: google.maps.LatLng[] = [],
            relations: number[] = [];

        for (; p < state.positions.length; p++) {
            position = state.positions[p];

            if (!position.found) {
                position.lat = state.lat + position.radius * Math.cos(position.radians);
                position.lng = state.lng + position.radius * Math.sin(position.radians);

                //new google.maps.Marker({position: {lat: position.lat, lng: position.lng}, label: '' + computation.cycle, map: isochrone.map.map});
                destinations.push(new google.maps.LatLng(position.lat, position.lng));
                relations.push(p);
            }
        }

        if (!destinations.length) {
            return this.computationCallback(state, 'OK');
        }

        this.service.getDistanceMatrix(
            {
                origins: [new google.maps.LatLng(state.lat, state.lng)],
                destinations: destinations,
                travelMode: state.mode,
                unitSystem: state.system,
            }, (data, result) => {
                this.getDistanceMatrixCallback(state, data, result, destinations, relations)
            });
    };

    private getDistanceMatrixCallback(state: IIsochroneState, data: google.maps.DistanceMatrixResponse, result: google.maps.DistanceMatrixStatus, destinations: google.maps.LatLng[], relations: number[]) {
        if (result !== google.maps.DistanceMatrixStatus.OK) {
            if (result === google.maps.DistanceMatrixStatus.OVER_QUERY_LIMIT && state.errors++ <= 10) {

                state.cycle--;
                //state.req = Math.max(0.5, state.requests - 0.5);
                //setTimeout(() => { this.cycle(); }, 2000);

                this.cycle(state);

                return false;
            }

            return this.computationCallback(state, result);
        }

        if ((typeof data.rows[0].elements) === 'undefined' || data.rows[0].elements.length !== destinations.length) {
            return this.computationCallback(state, 'LENGTH');
        }

        for (var i = 0; i < data.rows[0].elements.length; i++) {
            var d = data.rows[0].elements[i],
                value = 0,
                position = state.positions[relations[i]],
                minWeight = 0,
                maxWeight = 0;

            if (d.status !== google.maps.DistanceMatrixElementStatus.OK) {
                continue;
            }

            value = parseFloat(d[state.type].value);
            if (value < state.value && // value is lower than expected
                (!position.min.radius || // no minimum for now
                    position.radius > position.min.radius && value > position.min.value)) // value and radius are higher than minimum
            {
                position.min.radius = position.radius;
                position.min.value = value;
            }

            if (value > state.value && // value is higher than expected
                (!position.max.radius || // no maximum for now
                    position.radius < position.max.radius && value < position.max.value)) // value and radius are lower than maximum
            {
                position.max.radius = position.radius;
                position.max.value = value;
            }

            /* Accepted match */
            if (Math.abs(value - state.value) / state.value < state.precision) {
                position.found = true;
            }
            else {
                /* Recompute radius */
                if (!position.min.radius) {
                    /* Position is relative to the max found: apply «règle de trois» (Cross-multiplication) */
                    position.radius = position.max.radius * state.value / position.max.value;
                }
                else if (!position.max.radius) {
                    /* Position is relative to the min found: apply «règle de trois» (Cross-multiplication) */
                    position.radius = position.min.radius * state.value / position.min.value;
                }
                else {
                    /* Use «moyenne pondérée» (weighted arithmetic mean) */
                    /* weights are the difference between expected value and min/max values, inversed (less the distance is best) */
                    minWeight = 1 / Math.abs(position.min.value - state.value);
                    maxWeight = 1 / Math.abs(position.max.value - state.value);

                    position.radius = (position.min.radius * minWeight + position.max.radius * maxWeight) / (minWeight + maxWeight);
                }
            }
        }

        // Update UI with what we have so far
        state.callback("Processing", state.positions);

        // CJE ARTIFICIAL DELAY TO SEE WHAT ITS DOING
        //setTimeout(() => { this.cycle(); }, 1000 /*/ this.requests*/);
        this.cycle(state);
    }

    private computationCallback(state: IIsochroneState, status: google.maps.DistanceMatrixStatus | string, message: string = null) {
        message = message || '';

        if (message) {
            this.log(message);
        }

        if (state.positions.length) {
            state.positions.push(state.positions[0]); // Close the polygon for easier drawing
        }

        state.callback(status, state.positions);

        return false;
    }

    private log(text) {
        if (/*this.debug ||*/ true) {
            console.log("Debug.Log:", text);
        }
    };
};
